/**
 * StaffWorkshopHistory — the workshop-assignment list on the staff detail page.
 *
 * Split out of StaffEditPage (BON-398) along with the expand/collapse state it
 * owns; nothing here changed in the move.
 */
import { Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { memo, useState } from 'react';

import type { StaffMemberDetail } from '../../api/staff';
import { formatVenueDate } from '../../utils/venueTime';

type Workshop = NonNullable<StaffMemberDetail['workshops']>[number];

const COLLAPSED_COUNT = 5;

function statusBadge(status: string) {
  const map: Record<string, string> = {
    scheduled: 'bg-moss-50 dark:bg-moss-900/20 text-moss-700 dark:text-moss-400',
    completed: 'bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400',
    cancelled: 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400',
  };
  return map[status] ?? 'bg-stone-100 dark:bg-stone-800 text-stone-500';
}

function WorkshopRow({ w }: { w: Workshop }) {
  const upcoming = new Date(w.startDatetime) >= new Date();
  return (
    <div className="py-2.5 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-bark-900 dark:text-stone-100 truncate">{w.title}</p>
        <p className="text-xs text-stone-400 dark:text-stone-500 truncate">{w.venueName}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span
          className={`text-xs px-1.5 py-0.5 rounded-full capitalize ${
            w.position === 'teacher'
              ? 'bg-moss-50 dark:bg-moss-900/20 text-moss-700 dark:text-moss-400'
              : 'bg-water-50 dark:bg-water-900/20 text-water-700 dark:text-water-400'
          }`}
        >
          {w.position}
        </span>
        {/* Venue-local date. The `upcoming` check above stays on the absolute
            instant — that is a comparison, not a display (BON-386). */}
        <span className="text-xs text-stone-400 w-16 text-right">
          {formatVenueDate(w.localStartDatetime, 'MMM d')}
        </span>
        <span className={`text-xs px-1.5 py-0.5 rounded-full capitalize ${statusBadge(w.status)}`}>
          {upcoming && w.status === 'scheduled' ? 'upcoming' : w.status}
        </span>
      </div>
    </div>
  );
}

// memo: the parent re-renders on every form keystroke while `workshops` never
// changes mid-edit — without it a long history re-filters and re-renders per key.
export default memo(StaffWorkshopHistory);

function StaffWorkshopHistory({ workshops }: { workshops: Workshop[] }) {
  const [expanded, setExpanded] = useState(false);

  const now = new Date();
  const upcomingCount = workshops.filter((w) => new Date(w.startDatetime) >= now).length;
  const visible = expanded ? workshops : workshops.slice(0, COLLAPSED_COUNT);

  return (
    <section className="bg-white dark:bg-[#1e2521] rounded-2xl border border-stone-200 dark:border-stone-700 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-bark-900 dark:text-stone-100">
          <Calendar size={14} className="text-moss-600 dark:text-moss-400" />
          Workshops
        </h2>
        <div className="flex gap-2 text-xs text-stone-500 dark:text-stone-400">
          <span>{upcomingCount} upcoming</span>
          <span>·</span>
          <span>{workshops.length - upcomingCount} past</span>
        </div>
      </div>

      {workshops.length === 0 ? (
        <p className="text-sm text-stone-400 italic">No workshop assignments yet.</p>
      ) : (
        <>
          <div className="divide-y divide-stone-100 dark:divide-stone-800">
            {visible.map((w) => (
              <WorkshopRow key={`${w.id}-${w.position}`} w={w} />
            ))}
          </div>
          {workshops.length > COLLAPSED_COUNT && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-3 flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700 dark:hover:text-stone-200 transition"
            >
              {expanded ? (
                <>
                  <ChevronUp size={12} /> Show less
                </>
              ) : (
                <>
                  <ChevronDown size={12} /> Show {workshops.length - COLLAPSED_COUNT} more
                </>
              )}
            </button>
          )}
        </>
      )}
    </section>
  );
}
